import React, { useEffect } from 'react';
import { Link } from "react-router-dom";
import { FiActivity, FiLayers, FiPackage } from "react-icons/fi";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from './Header';
import Footer from './Footer';
import "../App.css"; // Ensure global styles are applied
import "./MarketingPages.css";

const HERO_BG_SRC = `${process.env.PUBLIC_URL || ''}/${encodeURI(
  'Glowing lines and vibrant gradients.png'
)}`;

const HERO_PRODUCT_IMG_SRC = `${process.env.PUBLIC_URL || ''}/img/product/shokiq_3third-imageb-d.png`;

const Landing = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    useEffect(() => {
        if (user) {
          navigate('/home');
        }
      }, [user, navigate]);

    
  return (
    <div className="page-flow-wrap">
      <Header />
      <div className="landing-container">
      {/* Hero Section */}
      <section
        className="section alternate-section landing-hero"
        style={{ backgroundImage: `url("${HERO_BG_SRC}")` }}
      >
        <div className="section-content-wrapper">
          <div className="section-content">
            <h1 className="hero-title">
              <span>analyze your suspension, gain real insight</span>
            </h1>
            <p className="hero-description">
              Purpose-built hardware captures how your fork and shock move on every run. The ShokIQ portal turns that telemetry into clear metrics—stroke usage, balance, events, and trends—so you can set up, tune, and ride with confidence.
            </p>
            <div className="auth-buttons">
              <Link to="/pre-order" className="auth-link auth-btn filled">
                Pre-order — $599
              </Link>
              <Link to="/login" className="auth-link auth-btn outlined">
                Sign in to portal
              </Link>
            </div>
            <p className="hero-description" style={{ marginTop: '12px', fontSize: '0.95em', opacity: 0.9 }}>
              Already have a device or account? Use <strong>Sign in to portal</strong> to upload rides and open your analytics. New here? <Link to="/login?signup=true" className="support-link">create an account</Link> after you receive your hardware.
            </p>
          </div>
          <div className="section-image">
            <img
              src={HERO_PRODUCT_IMG_SRC}
              alt="ShokIQ logo and hardware kit: data logger, linear travel sensors, mounts, and orange sensor cables"
            />
          </div>
        </div>
      </section>

      <section className="landing-value-strip" aria-label="Why ShokIQ">
        <div className="landing-value-strip-inner">
          <div className="landing-value-col">
            <div className="landing-value-icon" aria-hidden>
              <FiActivity />
            </div>
            <h3 className="landing-value-title">Actionable, not toy-grade</h3>
            <p className="landing-value-text">
              Metrics tied to real tuning moves—sag balance, stroke depth, velocities, events.
            </p>
          </div>
          <div className="landing-value-col">
            <div className="landing-value-icon" aria-hidden>
              <FiLayers />
            </div>
            <h3 className="landing-value-title">Depth when you want it</h3>
            <p className="landing-value-text">
              Compare laps, overlay video, and dig into stats without living in a spreadsheet.
            </p>
          </div>
          <div className="landing-value-col">
            <div className="landing-value-icon" aria-hidden>
              <FiPackage />
            </div>
            <h3 className="landing-value-title">Built for MTB</h3>
            <p className="landing-value-text">
              Logger, potentiometers, mounts, and platform access in one pre-order package.
            </p>
          </div>
        </div>
      </section>

      <section className="section landing-how-it-works" aria-labelledby="landing-how-heading">
        <div className="section-content-wrapper">
          <div className="section-image">
            <img src="img/overlay.png" alt="Review a run with fork and shock traces alongside video" />
          </div>
          <div className="section-content">
            <h2 id="landing-how-heading" className="section-title">
              How does it work?
            </h2>
            <p className="section-description landing-how-lede">
              Install once, ride like you normally would, then use the portal to turn recordings into decisions—without turning your weekend into a spreadsheet project.
            </p>
            <ol className="landing-how-steps">
              <li>
                <span className="landing-how-step-title">Attach the hardware and record a real run</span>
                <p>
                  Mount the logger and travel sensors, set sag and damping the way you would for this bike and trail, and capture a lap
                  or a full run. ShokIQ records fork and shock position through the ride—no dyno, no guesswork about whether the file is
                  usable.
                </p>
              </li>
              <li>
                <span className="landing-how-step-title">Get a clear summary of what happened</span>
                <p>
                  Upload and land on the signals we actually use when tuning—not a wall of charts you have to decode first. Your summary
                  centers on:
                </p>
                <ul className="landing-how-metrics">
                  <li>Dynamic sag and front vs rear stroke balance</li>
                  <li>Compression and rebound velocities (including high-signal percentiles)</li>
                  <li>Bottom-outs, top-out behavior, stroke depth, and stroke counts</li>
                  <li>Event-style cues that tie those numbers to how the bike was loaded through the run</li>
                </ul>
                <p>
                  When it is enabled for your account, optional AI insight sits next to that summary: short, run-specific notes on what
                  stands out—framed so you can decide what to verify or try next, without corporate fluff.
                </p>
              </li>
              <li>
                <span className="landing-how-step-title">Dig into events, then tune for the trail and the feel you want</span>
                <p>
                  Move from averages to moments: landings, hard braking, rough sections, and line choice show up in the traces. Compare
                  sessions, sync video if you use it, and relate numbers to how the bike felt—so the next change targets a specific
                  behavior on a specific trail, whether you are chasing grip, support, comfort, or pace.
                </p>
              </li>
            </ol>
          </div>
        </div>
      </section>

      <section className="section alternate-section">
        <div className="section-content-wrapper">
          <div className="section-content">
            <h2 className="section-title">Compare sessions, spot what changed</h2>
            <p className="section-description">
              Line up laps or whole rides and see how setup tweaks, tire choice, or track conditions show up in the data. ShokIQ makes it easy to relate feel on the bike to measurable suspension behavior.
            </p>
          </div>
          <div className="section-image">
            <img src="img/compare.png" alt="Compare suspension sessions" />
          </div>
        </div>
      </section>

      <section className="section">
        <div className="section-content-wrapper">
          <div className="section-image">
            <img src="/img/datasource.png" alt="ShokIQ hardware and data" />
          </div>
          <div className="section-content">
            <h2 className="section-title">Built for the bike, open where you need it</h2>
            <p className="section-description">
              The ShokIQ sensor package is designed for real-world MTB and gravity use. The portal ingests your recordings and can work alongside common formats when you want GPS or ride files in the same workflow.
            </p>
          </div>
        </div>
      </section>

      <section className="section alternate-section">
        <div className="section-content-wrapper">
          <div className="section-content">
            <h2 className="section-title">Share insight with your crew</h2>
            <p className="section-description">
              Export, review, and collaborate with a mechanic or coach using the same clear charts and stats—so adjustments are driven by evidence, not guesswork.
            </p>
          </div>
          <div className="section-image">
            <img src="/img/collab.png" alt="Collaborate on suspension setup" />
          </div>
        </div>
      </section>

      <section className="section">
        <div className="section-content-wrapper">
          <div className="section-image">
            <img src="/img/letsgo.png" alt="Get started with ShokIQ" />
          </div>
          <div className="section-content">
            <h2 className="section-title">Ready to ride smarter?</h2>
            <p className="section-description">
              Order ShokIQ for your bike, then use the portal to register, upload, and dig into every run.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
              <Link to="/pre-order" className="primary-btn">
                Pre-order — $599
              </Link>
              <Link to="/login" className="auth-link auth-btn outlined" style={{ margin: 0 }}>
                Portal sign in
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section
        className="section alternate-section landing-tuning-philosophy-section"
        aria-labelledby="landing-tuning-philosophy-heading"
      >
        <div className="section-content-wrapper">
          <div className="section-content">
            <h2 id="landing-tuning-philosophy-heading" className="section-title">
              Tuning philosophy
            </h2>
            <p className="section-description">
              The <strong>tuning philosophy</strong> page is a free, ShokIQ-specific resource: we explain how suspension metrics map to
              real setup choices—sag balance, stroke usage, velocities, stroke depth, and stroke counts—using the same mental model as
              the Basic Stats workflow in the portal. Topics are organized as expandable FAQs so you can skim or drill into what matters
              for your next ride.
            </p>
            <p className="section-description" style={{ marginTop: '0.75rem' }}>
              For orders, accounts, or hardware questions, use{' '}
              <Link to="/support" className="support-link">
                Support
              </Link>
              . Signed-in riders can open <strong>Help</strong> for walkthroughs and <strong>Tuning guide</strong> (bottom of the
              sidebar) for the full measured-tuning FAQ.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', marginTop: '1.25rem' }}>
              <Link to="/philosophy" className="auth-link auth-btn outlined" style={{ margin: 0 }}>
                Tuning philosophy
              </Link>
            </div>
          </div>
        </div>
      </section>

      </div>
      <Footer />
    </div>
  );
};

export default Landing;
